import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import TextEditor from "../TextEditor/TextEditor";
import { TextEditorFields } from "../Shared/TextEditorFields";
import { ProjectJobDescription } from "../../Models";
import useGraphApiToken from "../../hooks/useGraphApiToken";
import { SearchUsers, GraphUser } from "../../api/microsoft.api";

export type JobDescriptionPayload = {
  title: string;
  content: string;
  isActive: boolean;
  assigneeEmails: string | null;
};

type SelectedAssignee = {
  id: string;
  displayName: string;
  email: string;
};

const parseAssigneeEmails = (assigneeEmails?: string | null): SelectedAssignee[] => {
  if (!assigneeEmails) return [];
  return assigneeEmails
    .split(",")
    .map((email) => email.trim())
    .filter(Boolean)
    .map((email) => ({ id: email, displayName: email, email }));
};

export default function JobDescriptionModal({
  initialData,
  onSave,
  onClose,
}: {
  initialData?: ProjectJobDescription;
  onSave: (payload: JobDescriptionPayload) => Promise<void>;
  onClose: () => void;
}) {
  const [open, setOpen] = useState(true);
  const [title, setTitle] = useState(initialData?.title ?? "");
  const [content, setContent] = useState(initialData?.content ?? "");
  const [showTextEditor, setShowTextEditor] = useState(false);
  const [textEditorType, setTextEditorType] = useState("");
  const [saving, setSaving] = useState(false);

  // People currently working on this JD/project — same Microsoft Graph search
  // used to pick interviewers when creating/editing interview slots.
  const [selectedAssignees, setSelectedAssignees] = useState<SelectedAssignee[]>(
    parseAssigneeEmails(initialData?.assigneeEmails)
  );
  const [assigneeSearch, setAssigneeSearch] = useState("");
  const [assigneeSearchResults, setAssigneeSearchResults] = useState<GraphUser[]>([]);
  const [isSearchingAssignees, setIsSearchingAssignees] = useState(false);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const graphApiToken = useGraphApiToken();

  useEffect(() => {
    if (!open) {
      localStorage.removeItem("Job description");
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (assigneeSearch.length < 2) {
      setAssigneeSearchResults([]);
      setShowAssigneeDropdown(false);
      return;
    }
    const timer = setTimeout(async () => {
      if (!graphApiToken) return;
      setIsSearchingAssignees(true);
      try {
        const results = await SearchUsers(graphApiToken, assigneeSearch);
        setAssigneeSearchResults(results);
        setShowAssigneeDropdown(results.length > 0);
      } catch {
        setAssigneeSearchResults([]);
        setShowAssigneeDropdown(false);
      } finally {
        setIsSearchingAssignees(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [assigneeSearch, graphApiToken]);

  const addAssignee = (user: GraphUser) => {
    const email = user.mail || user.userPrincipalName;
    if (!email) return;
    if (selectedAssignees.some((p) => p.id === user.id)) return;
    setSelectedAssignees((prev) => [
      ...prev,
      { id: user.id, displayName: user.displayName, email },
    ]);
    setAssigneeSearch("");
    setShowAssigneeDropdown(false);
    setAssigneeSearchResults([]);
  };

  const removeAssignee = (id: string) => {
    setSelectedAssignees((prev) => prev.filter((p) => p.id !== id));
  };

  const handleSave = async () => {
    if (!title.trim() || saving) return;
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        content,
        isActive: initialData?.isActive ?? true,
        assigneeEmails:
          selectedAssignees.length > 0
            ? selectedAssignees.map((p) => p.email).join(", ")
            : null,
      });
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const canSave = title.trim().length > 0 && !saving;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={() => setOpen(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-400 bg-opacity-50 dark:bg-opacity-20 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0 h-full">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full h-[80%] overflow-y-auto flex flex-col max-w-3xl dark:bg-darkbglight">
                <h2 className="font-bold text-gray-600 my-4 text-sm text-left pl-4 border-b-2 pb-2 dark:text-white border-gray-500">
                  {initialData ? "Edit Job Description" : "Create Job Description"}
                </h2>
                {showTextEditor ? (
                  <TextEditor
                    textEditorType={textEditorType}
                    setShowTextEditor={setShowTextEditor}
                    setJobDescription={setContent}
                    defaultStateValue={content}
                    inputType=""
                    currentProcess={undefined}
                  />
                ) : (
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4 flex-grow dark:bg-darkbglight space-y-4">
                    <div>
                      <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-400">
                        Title
                        <span className="text-red-500"> *</span>
                      </label>
                      <input
                        type="text"
                        className="mt-2 block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-1 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 dark:bg-transparent dark:text-gray-300 dark:ring-gray-500"
                        placeholder="e.g. Software Engineer - DryDocking Module"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-400">
                        People working on this project
                      </label>

                      {selectedAssignees.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {selectedAssignees.map((person) => (
                            <div
                              key={person.id}
                              className="flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 border border-indigo-200 rounded-full dark:bg-indigo-900/30 dark:border-indigo-700"
                            >
                              <div className="w-4 h-4 bg-indigo-600 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0">
                                {person.displayName.charAt(0).toUpperCase()}
                              </div>
                              <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
                                {person.displayName}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeAssignee(person.id)}
                                className="text-indigo-400 hover:text-red-500 transition-colors"
                                aria-label={`Remove ${person.displayName}`}
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="relative mt-2">
                        <input
                          type="text"
                          value={assigneeSearch}
                          onChange={(e) => setAssigneeSearch(e.target.value)}
                          onBlur={() => setTimeout(() => setShowAssigneeDropdown(false), 150)}
                          className="w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-1 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 dark:bg-transparent dark:text-gray-300 dark:ring-gray-500"
                          placeholder="Search by name to add someone…"
                        />
                        {isSearchingAssignees && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <svg className="animate-spin h-3.5 w-3.5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                            </svg>
                          </div>
                        )}

                        {showAssigneeDropdown && (
                          <div
                            className="absolute z-50 w-full mt-1 bg-white dark:bg-darkbglight border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden"
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            {assigneeSearchResults.filter(
                              (r) => !selectedAssignees.some((s) => s.id === r.id)
                            ).length === 0 ? (
                              <div className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 text-center">
                                No matching users found
                              </div>
                            ) : (
                              <ul className="max-h-40 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
                                {assigneeSearchResults
                                  .filter((r) => !selectedAssignees.some((s) => s.id === r.id))
                                  .map((user) => (
                                    <li
                                      key={user.id}
                                      onMouseDown={() => addAssignee(user)}
                                      className="flex items-center gap-2.5 px-3 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 cursor-pointer transition-colors"
                                    >
                                      <div className="w-7 h-7 bg-indigo-600 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                                        {user.displayName?.charAt(0).toUpperCase()}
                                      </div>
                                      <div className="min-w-0 flex-1 text-left">
                                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                                          {user.displayName}
                                        </p>
                                        <p className="text-xs text-gray-400 truncate">
                                          {user.mail || user.userPrincipalName}
                                        </p>
                                      </div>
                                    </li>
                                  ))}
                              </ul>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <TextEditorFields
                      label="Job description"
                      state={content}
                      setTextEditorType={setTextEditorType}
                      setShowTextEditor={setShowTextEditor}
                    />
                  </div>
                )}
                {!showTextEditor && (
                  <div className="bg-gray-50 px-4 py-3 flex flex-row items-center justify-between mt-auto dark:bg-darkbglight">
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 text-xs py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={!canSave}
                      onClick={handleSave}
                      className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm sm:ml-3 sm:w-auto ${
                        canSave
                          ? "bg-blue-600 text-white hover:bg-blue-500"
                          : "cursor-not-allowed bg-gray-100 text-black"
                      }`}
                    >
                      {initialData ? "Update" : "Create"}
                    </button>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
